import { useId, type ComponentPropsWithRef, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

export interface SwitchProps extends Omit<ComponentPropsWithRef<'input'>, 'type' | 'children'> {
  /** Etykieta po lewej stronie przelacznika. */
  label?: ReactNode;
  /** Opis pod etykieta. */
  description?: ReactNode;
}

/**
 * Przelacznik wlacz/wylacz (np. „Mecz o 3. miejsce”).
 *
 * Pod spodem to nadal `<input type="checkbox">` — semantyka i obsluga
 * klawiatury zostaja natywne, zmieniamy tylko wyglad.
 */
export function Switch({ className, label, description, id, disabled, ...props }: SwitchProps) {
  const generatedId = useId();
  const controlId = id ?? generatedId;

  const control = (
    <span className="relative inline-flex shrink-0 items-center">
      <input
        id={controlId}
        type="checkbox"
        role="switch"
        disabled={disabled}
        className={cn(
          'peer h-6 w-11 cursor-pointer appearance-none rounded-full',
          'bg-neutral-300 transition-colors duration-200',
          'checked:bg-brand',
          'focus-visible:ring-brand/35 focus-visible:ring-2 focus-visible:ring-offset-2',
          'focus-visible:ring-offset-canvas focus-visible:outline-none',
          'disabled:cursor-not-allowed disabled:opacity-45',
          className,
        )}
        {...props}
      />
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute left-0.5 size-5 rounded-full bg-white shadow-sm',
          'transition-transform duration-200 peer-checked:translate-x-5',
        )}
      />
    </span>
  );

  if (!label && !description) {
    return control;
  }

  return (
    <div className={cn('flex items-start gap-3', disabled && 'opacity-60')}>
      <div className="flex flex-col gap-0.5">
        {label && (
          <label
            htmlFor={controlId}
            className={cn(
              'text-fg text-sm font-medium',
              disabled ? 'cursor-not-allowed' : 'cursor-pointer',
            )}
          >
            {label}
          </label>
        )}
        {description && <p className="text-fg-subtle text-xs">{description}</p>}
      </div>
      <span className="ml-auto flex h-5 items-center">{control}</span>
    </div>
  );
}
